import type { ReactNode } from 'react';
import { motion, token } from '@bp-files/ui';

/**
 * USR-14's enrollment gate, as a padlock (ADR-0063).
 *
 * **It was a `<Switch>`, and a switch was the wrong shape for it.** That
 * control means a preference with two equally ordinary positions — notify me,
 * or do not. This one is a security gate: locked is the resting state every
 * member sits in, and unlocking it opens a window in which somebody can
 * register a new credential. A padlock says which of the two positions is the
 * exception; a switch says neither.
 *
 * **The label names the act, the title names the state.** `users.copy.ts`'s own
 * `enrollment` block has the argument at length — the short version is that a
 * switch announces "on"/"off" for itself and a padlock announces nothing, so
 * the accessible name has to carry a whole sentence and the safer sentence is
 * the instruction.
 *
 * **Colour carries the state as well as the glyph**, because a closed shackle
 * and an open one differ by a few pixels of arc at this size. Unlocked is
 * `bp-blue` — this system's one accent, and unlocked is the live, temporary,
 * needs-attention position. Locked is `bp-text-secondary`: ordinary, resting,
 * not a warning.
 *
 * Local to `Users` rather than a `packages/ui` composition: "enrollment" is a
 * concept from SPEC §9, and a design-system component that knew about it would
 * be a shared component with one caller's vocabulary baked in. The two glyphs
 * are Lucide `lock` and `lock-open`, hand-copied, as every glyph in this
 * workspace is — ADR-0053 says why there is no icon library here.
 */
export interface EnrollmentLockProps {
  /** `true` when enrollment is open — the exceptional position. */
  unlocked: boolean;
  onChange: (next: boolean) => void;
  /** The accessible name. Names the act this press performs, not the state. */
  label: string;
  /** The hover. Names the state, which the label deliberately does not. */
  title: string;
  /**
   * A change is in flight. Held at its current position and unpressable — the
   * `<Switch>` this replaces drew the same distinction from `disabled`, and for
   * the same reason: busy is "this is happening", disabled is "not yours".
   */
  busy?: boolean;
}

export function EnrollmentLock({
  unlocked,
  onChange,
  label,
  title,
  busy = false,
}: EnrollmentLockProps) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-busy={busy}
      title={title}
      disabled={busy}
      onClick={() => {
        // The caller is told which state was asked for, not that a press
        // happened — `Switch`'s contract, kept, so `MembersTab`'s and
        // The handlers did not have to change with the glyph.
        if (!busy) onChange(!unlocked);
      }}
      /*
        **`w-9` rather than `min-w-9`, and the difference is the whole of an
        alignment bug this once had.** There used to be a read-only branch here
        for the reader's own row (withdrawn by ADR-0100) drawn as a
        `<span className="flex …">` — a *block-level* flex container at
        `width: auto`, so it stretched to the cell and `justify-center` centred
        the glyph in the whole column, while this `<button>`, which a browser
        shrinks to fit, stayed 36px against the left edge. One column, two
        positions, decided by an intrinsic-sizing rule nobody wrote down. The
        fixed box makes placement the column's job, which is `align: 'center'`
        in `MembersTab`'s `enrollmentColumn`.
      */
      className="flex min-h-9 w-9 items-center justify-center rounded-md p-2 transition-colors hover:bg-elevated disabled:cursor-not-allowed disabled:opacity-60"
      style={{
        color: unlocked ? token('bp-blue') : token('bp-text-secondary'),
        transition: `color ${motion.fast}`,
      }}
    >
      {unlocked ? <LockOpenIcon /> : <LockIcon />}
    </button>
  );
}

/**
 * Lucide `lock` and `lock-open`, hand-copied.
 *
 * The body is identical in both and only the shackle moves, which is the whole
 * reason the colour above carries the state too: at 18px the difference between
 * the two paths is a shackle leaning off its left post.
 */
function LockGlyph({ children }: { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={18}
      height={18}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className="shrink-0"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" />
      {children}
    </svg>
  );
}

function LockIcon() {
  return (
    <LockGlyph>
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </LockGlyph>
  );
}

function LockOpenIcon() {
  return (
    <LockGlyph>
      <path d="M7 11V7a5 5 0 0 1 9.9-1" />
    </LockGlyph>
  );
}
